import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * Keeps the package-components section in sync with the selected variant.
 *
 * On a product page the variant picker fetches the whole product URL for the
 * new option values and hands the parsed document to every listener via
 * the product-select event (StandardEvents.productSelect). That document already contains this section
 * rendered by Liquid for the new variant, so all this element does is morph
 * itself from its counterpart — the prices and the saving are never
 * recalculated client-side.
 *
 * The event is dispatched on the variant picker, which lives in a different
 * shopify-section, so we listen on the document rather than on our own
 * section the way product-price.js does.
 */
class WolvPackageComponents extends HTMLElement {
  /** Phones collapse the per-component detail; from 750px up there is room to show it. */
  #compact = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    document.addEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
    this.#compact.addEventListener('change', this.#syncDetails);
    this.#syncDetails();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
    this.#compact.removeEventListener('change', this.#syncDetails);
  }

  /**
   * Liquid renders every detail open, so a visitor without JS never loses content
   * and search engines always see it. Here it is closed again on a small screen —
   * only at the breakpoint, never while someone is reading, so a card a visitor
   * opened themselves stays open.
   */
  #syncDetails = () => {
    for (const details of this.querySelectorAll('details.wolv-package__more')) {
      details.open = !this.#compact.matches;
    }
  };

  /**
   * @param {Event} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    // Horizon 4.x: the variant picker dispatches a product-select event whose
    // promise resolves with the freshly rendered product page (detail.html).
    // Selections inside product cards and quick add are not about this page.
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog')) return;

    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => this.#apply(result?.detail?.html))
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[wolv] product select rejected:', error);
      });
  };

  /**
   * @param {Document | undefined} html - The re-rendered product page.
   */
  #apply = (html) => {
    if (!html) return;

    const source = html.querySelector(
      `wolv-package-components[data-section-id="${this.dataset.sectionId}"]`
    );
    if (!source) return;

    morph(this, source);

    // The morphed markup arrives open again, so the small-screen state is reapplied.
    this.#syncDetails();
  };
}

if (!customElements.get('wolv-package-components')) {
  customElements.define('wolv-package-components', WolvPackageComponents);
}
